import { FC, useState, useMemo } from 'react';
import { Add, Close, Save } from '@mui/icons-material';
import {
  FormControlLabel,
  FormControl,
  FormLabel,
  FormHelperText,
  Grid,
  MenuItem,
  TextField,
  Box,
  ButtonGroup,
  Button,
  Divider,
  Radio,
  RadioGroup,
  Autocomplete,
  List,
  Typography
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useQuery } from 'react-query';
import { Form, Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  putJobHouseInfo,
  getGarageOptions,
  getDrivewaySides,
  getJobNeighborhoodSeriesOptions,
  getNeighborhoodSeriesColorExclusionOptions,
  getJobColorExclusionOptions
} from '../../../../fetch';
import { IDropdownResponse, IJobHouseInfo, IColorExclusions } from '../../../../models';
import { Loader, SMBSelect } from '../../../../components';
import { JobsExcludedColorsOptionList } from './jobs-excluded-colors-list';

interface IHouseInfo {
  houseInfo: IJobHouseInfo;
  isLoadingHouseInfo: boolean;
  setDirtyForm: (val: boolean) => void;
  refetchHouseInfo: () => void;
}

const HouseInfoSchema = Yup.object().shape({
  jobId: Yup.number(),
  jobCode: Yup.string(),
  neighborhoodSeriesId: Yup.number(),
  availableGarageOptions: Yup.array(),
  excludedColorSchemes: Yup.array(),
  drivewaySide: Yup.string(),
  canSalesSwapDrivewaySide: Yup.string()
});

export const HouseInfo: FC<IHouseInfo> = ({
  houseInfo,
  isLoadingHouseInfo,
  setDirtyForm,
  refetchHouseInfo
}) => {
  const history = useHistory();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { jobId }: { jobId: string } = useParams();
  const [selectedGarageOptionValues, setSelectedGarageOptionValues] = useState<IDropdownResponse[]>(
    []
  );
  const [inputSelectedGarageOptions, setInputSelectedGarageOptions] = useState('');

  const { isLoading: isLoadingNeighborhoodSeriesOptions, data: neighborhoodSeriesOptions } =
    useQuery<IDropdownResponse[], Error>(['getJobNeighborhoodSeriesOptions'], () =>
      getJobNeighborhoodSeriesOptions(jobId)
    );

  const {
    isLoading: isLoadingSeriesColorExclusionOptions,
    data: neighborhoodsSeriesColorExclusionOptions
  } = useQuery<IColorExclusions[], Error>(
    ['neighborhoodsSeriesExcludedColorOptions'],
    () => getNeighborhoodSeriesColorExclusionOptions(),
    {
      onSuccess: data => {},
      notifyOnChangeProps: 'tracked'
    }
  );

  const { data: jobColorExclusionOptions } = useQuery<number[], Error>(
    ['getJobColorExclusionOptions'],
    () => getJobColorExclusionOptions(jobId),
    {
      onSuccess: data => {},
      notifyOnChangeProps: 'tracked'
    }
  );

  const { isLoading: isLoadingDrivewaySides, data: drivewaySides } = useQuery<
    IDropdownResponse[],
    Error
  >(['getDrivewaySides'], () => getDrivewaySides());

  const { isLoading: isLoadingGarageOptions, data: garageOptions } = useQuery<
    IDropdownResponse[],
    Error
  >(['getGarageOptions'], () => getGarageOptions(), {
    onSuccess: d => {
      const options = d.filter(record => {
        return houseInfo?.availableGarageOptions?.includes(record.value);
      });
      setSelectedGarageOptionValues(options);
    },
    enabled: houseInfo !== undefined
  });
  const garageOptionsData = useMemo(() => garageOptions ?? [], [garageOptions]);

  return (
    <>
      {isLoadingHouseInfo && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingHouseInfo && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            ...houseInfo,
            neighborhoodSeriesId: houseInfo?.neighborhoodSeriesId ?? '',
            availableGarageOptions: houseInfo?.availableGarageOptions ?? [],
            excludedColorSchemes: houseInfo?.excludedColorSchemes ?? [],
            drivewaySide: houseInfo?.drivewaySide ?? '',
            canSalesSwapDrivewaySide:
              houseInfo?.canSalesSwapDrivewaySide === true
                ? 'Yes'
                : houseInfo?.canSalesSwapDrivewaySide === false
                ? 'No'
                : ''
          }}
          validationSchema={HouseInfoSchema}
          onSubmit={async (values, actions) => {
            try {
              const data: IJobHouseInfo = {
                ...values,
                canSalesSwapDrivewaySide: values.canSalesSwapDrivewaySide === 'Yes' ? true : false,
                neighborhoodSeriesId: values.neighborhoodSeriesId
                  ? Number(values.neighborhoodSeriesId)
                  : null
              };

              await putJobHouseInfo(parseInt(jobId), data);
              refetchHouseInfo();
              enqueueSnackbar('House information has been updated successfully!', {
                variant: 'success'
              });
            } catch (error) {
              enqueueSnackbar(
                error?.Detail ?? 'Something went wrong. House information was not updated.',
                {
                  variant: 'error'
                }
              );
            }
          }}
        >
          {({
            isSubmitting,
            values,
            setFieldValue,
            errors,
            touched,
            handleSubmit,
            dirty,
            isValid,
            handleBlur
          }) => {
            // This is also what Formik does in their official documentation.
            // https://stackoverflow.com/questions/61031464/setstate-called-in-render-prop-is-causing-a-react-warning
            // Also see https://github.com/jaredpalmer/formik/issues/1218
            if (dirty) {
              setTimeout(() => setDirtyForm(true), 0);
            } else {
              setTimeout(() => setDirtyForm(false), 0);
            }

            return (
              <>
                {isSubmitting && (
                  <Loader type="fullscreen" position="centered">
                    Saving...
                  </Loader>
                )}

                <Form onSubmit={handleSubmit}>
                  <Box mt={1}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              name="neighborhoodSeriesId"
                              label="Neighborhood Series"
                              id="neighborhoodSeriesId"
                              variant="outlined"
                              disabled={isLoadingNeighborhoodSeriesOptions}
                              value={values.neighborhoodSeriesId}
                              onChange={({ target: { value } }) => {
                                setFieldValue('neighborhoodSeriesId', value);
                              }}
                              onBlur={handleBlur}
                              error={errors && errors.neighborhoodSeriesId ? true : false}
                            >
                              {neighborhoodSeriesOptions &&
                                neighborhoodSeriesOptions.map((job, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={job.value}>
                                      {job.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                            {touched &&
                              touched.neighborhoodSeriesId &&
                              errors &&
                              errors.neighborhoodSeriesId && (
                                <FormHelperText error>Required</FormHelperText>
                              )}
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              name="drivewaySide"
                              label="Driveway Side"
                              id="drivewaySide"
                              variant="outlined"
                              disabled={isLoadingDrivewaySides}
                              value={values.drivewaySide}
                              onChange={({ target: { value } }) => {
                                setFieldValue('drivewaySide', value);
                              }}
                              onBlur={handleBlur}
                              error={errors && errors.drivewaySide ? true : false}
                            >
                              {drivewaySides &&
                                drivewaySides.map((driveway, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={driveway.value}>
                                      {driveway.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Autocomplete
                              multiple
                              id="availableGarageOptions"
                              value={selectedGarageOptionValues}
                              onChange={(event, newValue: any[]) => {
                                setSelectedGarageOptionValues(newValue);
                                setFieldValue(
                                  'availableGarageOptions',
                                  newValue.map(val => val.value)
                                );
                              }}
                              inputValue={inputSelectedGarageOptions}
                              onInputChange={(event, newInputValue) => {
                                setInputSelectedGarageOptions(newInputValue);
                              }}
                              options={garageOptionsData}
                              getOptionLabel={option => option.description}
                              filterSelectedOptions
                              renderOption={(props, option: any) => {
                                return (
                                  <li {...props} key={`${option.value}`}>
                                    {option.description}
                                  </li>
                                );
                              }}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  disabled={isLoadingGarageOptions}
                                  label={
                                    isLoadingGarageOptions
                                      ? 'Loading...'
                                      : 'Available Garage Options'
                                  }
                                  placeholder="Garage Options"
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl component="fieldset">
                              <FormLabel>Can Sales Swap Driveway Side</FormLabel>
                              <RadioGroup
                                sx={{
                                  position: 'flex',
                                  flexDirection: 'row',
                                  justifyContent: 'left',
                                  alignItems: 'start'
                                }}
                                aria-label="canSalesSwapDrivewaySide"
                                name="canSalesSwapDrivewaySide"
                                value={values.canSalesSwapDrivewaySide}
                                onChange={e =>
                                  setFieldValue('canSalesSwapDrivewaySide', e.target.value)
                                }
                              >
                                <FormControlLabel value={'Yes'} control={<Radio />} label={'Yes'} />
                                <FormControlLabel value={'No'} control={<Radio />} label={'No'} />
                              </RadioGroup>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box mt={1} mb={0.5}>
                    <Divider />
                  </Box>
                  <ButtonGroup>
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={jobId ? <Save /> : <Add />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        history.push('/development/jobs');
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Form>
                {isLoadingSeriesColorExclusionOptions && (
                  <Loader type="fullscreen" position="centered" />
                )}
                {!isLoadingSeriesColorExclusionOptions && (
                  <>
                    <Typography mt={2} variant="h5" color="primary">
                      Color Scheme Exclusions
                    </Typography>
                    <Box
                      margin={{
                        xs: '0 0 0 0',
                        sm: '0 0 0 1rem'
                      }}
                    >
                      <List
                        component="div"
                        aria-labelledby="nested-list-subheader"
                        className={classes.list}
                      >
                        {neighborhoodsSeriesColorExclusionOptions &&
                          neighborhoodsSeriesColorExclusionOptions
                            .filter(colorOption => {
                              return jobColorExclusionOptions?.includes(colorOption?.colorSchemeID);
                            })
                            .map(colorOption => (
                              <JobsExcludedColorsOptionList
                                key={`${colorOption.code}`}
                                colorOption={colorOption}
                                selections={houseInfo?.excludedColorSchemes}
                                refetch={refetchHouseInfo}
                              />
                            ))}
                      </List>
                    </Box>
                  </>
                )}
              </>
            );
          }}
        </Formik>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    marginTop: theme.spacing(1)
  },
  list: {
    maxHeight: `35rem`,
    overflow: 'auto',
    margin: theme.spacing(0.25, 0)
  }
}));
